import React from 'react';
import { ArrowUp, ArrowDown, X } from 'lucide-react';
import type { AttributeType } from '../../../api/types';

// ─── Shared styles (duplicated for self-containment) ─────────────────────

const inpSt: React.CSSProperties = {
  background: 'var(--glass-bg-card)', border: '1px solid rgba(255,255,255,0.10)',
  borderRadius: 'var(--radius-card)', color: 'var(--color-text)',
  fontSize: 'var(--text-sm)', padding: '0.5rem 0.75rem', outline: 'none', width: '100%',
};

const selSt: React.CSSProperties = {
  background: 'var(--glass-bg-card)', border: '1px solid rgba(255,255,255,0.10)',
  borderRadius: 'var(--radius-card)', color: 'var(--color-text)',
  fontSize: 'var(--text-sm)', padding: '0.5rem 0.625rem', outline: 'none',
  cursor: 'pointer', width: '100%',
};

const iconBtnSt: React.CSSProperties = {
  background: 'none', border: 'none', cursor: 'pointer',
  color: 'var(--color-text-muted)', padding: '0.25rem',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
};

// ─── Types ───────────────────────────────────────────────────────────────

const ATTR_TYPES: { value: AttributeType; label: string }[] = [
  { value: 'TEXT',    label: 'Testo' },
  { value: 'NUMBER',  label: 'Numero' },
  { value: 'BOOLEAN', label: 'Si / No' },
  { value: 'SELECT',  label: 'Scelta multipla' },
];

export interface AttrForm {
  key: string;
  attributeType: AttributeType;
  defaultValue: string;
  options: string;      // comma-separated for SELECT
  unit: string;
  isRequired: boolean;
  sortOrder: number;
}

export const emptyAttr = (sortOrder = 0): AttrForm => ({
  key: '', attributeType: 'TEXT', defaultValue: '', options: '', unit: '', isRequired: false, sortOrder,
});

// ─── AttributeRow ────────────────────────────────────────────────────────

export function AttributeRow({
  attr, index, total,
  onChange, onRemove, onMove,
}: {
  attr: AttrForm; index: number; total: number;
  onChange: (updated: AttrForm) => void;
  onRemove: () => void;
  onMove: (dir: 'up' | 'down') => void;
}) {
  const upd = <K extends keyof AttrForm>(key: K, val: AttrForm[K]) =>
    onChange({ ...attr, [key]: val });

  return (
    <div style={{
      background: 'var(--glass-bg-card)',
      border: '1px solid rgba(255,255,255,0.10)',
      borderRadius: 'var(--radius-card)',
      padding: '0.75rem',
      display: 'flex',
      flexDirection: 'column',
      gap: '0.5rem',
    }}>
      {/* Row 1: move + name + type + required + delete */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <button onClick={() => onMove('up')} disabled={index === 0}
            style={{ ...iconBtnSt, opacity: index === 0 ? 0.3 : 1 }}><ArrowUp size={11} /></button>
          <button onClick={() => onMove('down')} disabled={index === total - 1}
            style={{ ...iconBtnSt, opacity: index === total - 1 ? 0.3 : 1 }}><ArrowDown size={11} /></button>
        </div>

        <input
          placeholder="Nome attributo (es. Dimensione)"
          value={attr.key}
          onChange={e => upd('key', e.target.value)}
          style={{ ...inpSt, flex: 1 }}
        />

        <select value={attr.attributeType}
          onChange={e => upd('attributeType', e.target.value as AttributeType)}
          style={{ ...selSt, width: 150, flexShrink: 0 }}
        >
          {ATTR_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>

        <label style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', cursor: 'pointer', fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', whiteSpace: 'nowrap', flexShrink: 0 }}>
          <input type="checkbox" checked={attr.isRequired}
            onChange={e => upd('isRequired', e.target.checked)}
            style={{ accentColor: 'var(--color-primary)', cursor: 'pointer' }} />
          Obbl.
        </label>

        <button onClick={onRemove} style={{ ...iconBtnSt, color: 'var(--color-error)', flexShrink: 0 }}>
          <X size={14} />
        </button>
      </div>

      {/* Row 2: type-specific fields */}
      <div style={{ display: 'flex', gap: '0.5rem', paddingLeft: '1.5rem' }}>
        {attr.attributeType === 'SELECT' && (
          <div style={{ flex: 1 }}>
            <label style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', display: 'block', marginBottom: '0.25rem' }}>
              Opzioni (una per riga)
            </label>
            <textarea
              value={attr.options}
              onChange={e => upd('options', e.target.value)}
              placeholder={'30x30\n35x35\n40x40'}
              style={{ ...inpSt, height: 64, resize: 'vertical', fontFamily: 'inherit' }}
            />
            <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-faint)', marginTop: '0.2rem' }}>
              Una opzione per riga. La prima sara il default se non specificato.
            </p>
          </div>
        )}

        {(attr.attributeType === 'TEXT' || attr.attributeType === 'NUMBER') && (
          <div style={{ flex: 1 }}>
            <label style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', display: 'block', marginBottom: '0.25rem' }}>
              Valore default
            </label>
            <input
              type={attr.attributeType === 'NUMBER' ? 'number' : 'text'}
              value={attr.defaultValue}
              onChange={e => upd('defaultValue', e.target.value)}
              placeholder="Valore predefinito"
              style={{ ...inpSt }}
            />
          </div>
        )}

        {attr.attributeType === 'NUMBER' && (
          <div style={{ width: 100 }}>
            <label style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', display: 'block', marginBottom: '0.25rem' }}>
              Unita
            </label>
            <input
              value={attr.unit}
              onChange={e => upd('unit', e.target.value)}
              placeholder="cm / pag / ore"
              style={{ ...inpSt }}
            />
          </div>
        )}

        {attr.attributeType === 'BOOLEAN' && (
          <div style={{ flex: 1 }}>
            <label style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', display: 'block', marginBottom: '0.25rem' }}>
              Default
            </label>
            <select value={attr.defaultValue} onChange={e => upd('defaultValue', e.target.value)} style={{ ...selSt }}>
              <option value="false">No</option>
              <option value="true">Si</option>
            </select>
          </div>
        )}
      </div>
    </div>
  );
}
